import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { PAYWALL_FLOW } from "@/constants/paywallFlow";
import { useCompletedExperienceCount } from "@/hooks/useCompletedExperienceCount";
import { useGetActiveSubscriptions } from "pages/pricing/hooks/useGetActiveSubscriptions";

export type SoftPaywallVariant =
  | "default"
  | "specialOffer"
  | "lastFree"
  | "expiredTrialPaywall";

type SoftPaywallContextValue = {
  paywallFlow: string;
  /** Total number of created projects (regardless of status) */
  totalCount: number;
  /** Number of completed experiences */
  completedCount: number;
  /** How many free experiences remain (max 3) */
  experiencesLeft: number;
  /** Which variant of the soft paywall to display */
  variant: SoftPaywallVariant;
  /** Whether the soft paywall modal is currently open */
  showSoftPaywall: boolean;
  /** Whether this user is on a free plan and in a soft-paywall experiment group */
  isSoftPaywallEligible: boolean;
  /** Whether the user has exhausted all free experiences (trial expired) */
  isTrialExpired: boolean;
  /** Set the grace project id (allows viewing the project even at the 3-exp limit) */
  setGraceProjectId: (projectId: string) => void;
  /** Open the soft paywall modal (if conditions are met) */
  openSoftPaywall: () => void;
  /** Dismiss the soft paywall modal for the rest of the session */
  dismissSoftPaywall: () => void;
  /** Clear the dismissed state so the modal can show again (call when starting a new experience) */
  resetDismissed: () => void;
};

const SoftPaywallContext = createContext<SoftPaywallContextValue | null>(null);

export const SoftPaywallProvider = ({ children }: { children: ReactNode }) => {
  const { isFreeUser, isLoading: isSubscriptionLoading } =
    useGetActiveSubscriptions();

  const paywallFlow = PAYWALL_FLOW;

  // Counts can only be fetched once we know the user is in scope; we still
  // need to wait for them before opening the modal so the variant is stable.
  const canFetchCounts =
    !isSubscriptionLoading &&
    isFreeUser &&
    (paywallFlow === "hard_no_card_3d" || paywallFlow === "soft_3_exp");

  const {
    completedCount,
    totalCount,
    isLoading: isCountsLoading,
  } = useCompletedExperienceCount(canFetchCounts);

  // Full eligibility — used by openSoftPaywall and downstream consumers — only
  // flips true after subscriptions and counts have both resolved.
  const isSoftPaywallEligible = canFetchCounts && !isCountsLoading;

  // Using totalCount (created projects) rather than completedCount so that
  // abandoned/in-progress projects still count toward the free limit.
  // completedCount is kept in the context in case paywall rules change in the future.
  const experiencesLeft = Math.max(0, 3 - totalCount);

  const isTrialExpired = isSoftPaywallEligible && experiencesLeft <= 0;

  // Track whether the current modal open was triggered right after creating an experience
  const [isPostCreation, setIsPostCreation] = useState(false);

  const variant: SoftPaywallVariant = useMemo(() => {
    if (experiencesLeft <= 0) {
      // Right after 3rd experience creation → lastFree; subsequent visits → expiredTrialPaywall
      return isPostCreation ? "lastFree" : "expiredTrialPaywall";
    }
    if (experiencesLeft === 1) return "specialOffer";
    return "default";
  }, [experiencesLeft, isPostCreation]);

  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem("softPaywallDismissed") === "true"
  );
  const [showSoftPaywall, setShowSoftPaywall] = useState(false);

  const setGraceProjectId = useCallback((projectId: string) => {
    // Write to sessionStorage for redirection.ts (runs outside React)
    sessionStorage.setItem("soft3ExpGraceProjectId", projectId);
  }, []);

  const resetDismissed = useCallback(() => {
    setDismissed(false);
    setIsPostCreation(true);
    sessionStorage.removeItem("softPaywallDismissed");
  }, []);

  // openSoftPaywall is intentionally a stable, no-arg state setter. The actual
  // "should be visible?" decision is derived during render below, combining
  // eligibility + dismissed + isTrialExpired. Keeping the callback identity
  // stable matters because consumer effects list it in their deps — if it
  // changed every time `dismissed` flipped, dismissing would re-fire those
  // effects and re-open the modal in the same render cycle (MED-6554).
  const openSoftPaywall = useCallback(() => {
    setShowSoftPaywall(true);
  }, []);

  const dismissSoftPaywall = useCallback(() => {
    setDismissed(true);
    setIsPostCreation(false);
    setShowSoftPaywall(false);
    sessionStorage.setItem("softPaywallDismissed", "true");
  }, []);

  // Derive the actual "show modal?" boolean from all the gating conditions.
  // Centralizing the rules here (instead of inside openSoftPaywall) lets that
  // callback stay reference-stable while still preserving the original intent:
  //   - eligibility is required (defensive against mid-session upgrades that
  //     refetch the subscription and reclassify the user as paid).
  //   - dismissed blocks the modal, except for expiredTrialPaywall, which
  //     re-shows on the next mount/navigation but not within the same render
  //     cycle as the dismissal (MED-6554).
  const derivedShowSoftPaywall =
    showSoftPaywall && isSoftPaywallEligible && (isTrialExpired || !dismissed);

  const value = useMemo<SoftPaywallContextValue>(
    () => ({
      paywallFlow,
      totalCount,
      completedCount,
      experiencesLeft,
      variant,
      showSoftPaywall: derivedShowSoftPaywall,
      isSoftPaywallEligible,
      isTrialExpired,
      setGraceProjectId,
      openSoftPaywall,
      dismissSoftPaywall,
      resetDismissed,
    }),
    [
      paywallFlow,
      totalCount,
      completedCount,
      experiencesLeft,
      variant,
      derivedShowSoftPaywall,
      isSoftPaywallEligible,
      isTrialExpired,
      setGraceProjectId,
      openSoftPaywall,
      dismissSoftPaywall,
      resetDismissed,
    ]
  );

  return (
    <SoftPaywallContext.Provider value={value}>
      {children}
    </SoftPaywallContext.Provider>
  );
};

export const useSoftPaywall = (): SoftPaywallContextValue => {
  const ctx = useContext(SoftPaywallContext);
  if (!ctx) {
    throw new Error("useSoftPaywall must be used within a SoftPaywallProvider");
  }
  return ctx;
};
